import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as T from "../../api/compliance/index.types";
import { IGetTopLiveLosersAndTopLiveSimTakersResponse } from "../../api/compliance/index.types";

export interface IComplianceSearchAccountsState {
  accounts_table: {
    data: T.IGetAllEntriesResponse["data"]["accounts_table"];
    loading: boolean;
  };
  logins: {
    data: T.IGetCustomersLoginsResponse["data"]["logins"];
    loading: boolean;
  };
  uniqueReqIp: {
    data: T.IUniqueReqIpResponse["data"]["usageData"];
    loading: boolean;
  };
  paymentCards: {
    data: T.IGetPaymentCardsResponse["data"]["paymentCards"];
    loading: boolean;
  };
  plans: {
    data: T.IGetAccountPlansDataResponse["data"];
    loading: boolean;
  };
  plansStatus: {
    data: T.IGetAccountPlansStatusResponse["data"]["accounts"];
    loading: boolean;
  };
  purchasesHistory: {
    data: T.TGetAccountsPurchasesResponse["data"]["purchasesHistory"];
    loading: boolean;
  };
  resetsRebillsNewPurchases: {
    data: T.IGetAccountsResetsRebillsNewPurchasesResponse["data"]["accounts"];
    loading: boolean;
  };
  basicAccountDetailsAndSalesValue: {
    data: T.IBasicAccountDetailsAndSalesValueResponse["data"]["accounts"];
    loading: boolean;
  };
  searchOnNameVariations: {
    data: T.IGetAllEntriesRequestByValuesResponse["data"];
    loading: boolean;
  };
  searchOnPhoneVariations: {
    data: T.IGetAllEntriesRequestByValuesResponse["data"];
    loading: boolean;
  };
  rithmicAccounts: {
    data: T.IGetEvaluationsRithmicAccountsResponse["data"];
    loading: boolean;
  };
  watchlistSubmissionToProp: {
    data: T.IGetPassesResponse["data"];
    loading: boolean;
  };
  watchlistWithdrawals: {
    loading: boolean;
    data: T.IWithdrawalsResponse["data"];
  };
  watchlistFailed: {
    loading: boolean;
    data: T.IFailedResponse["data"];
  };
  watchlistLiveSimTif: {
    loading: boolean;
    data: T.IGetWatchlistLiveSimTifResponse["data"];
  };
  watchlistNotes: {
    loading: boolean;
    data: T.IGetSearchNotesResponse["data"];
  };
  heliosAccountsInvestigation: {
    loading: boolean;
    data: T.IGetHeliosRelatedSearchResponse["data"];
  };
  pnlCounter: {
    loading: boolean;
    data: T.IGetPnlDataResponse["data"];
  };
  heliosAndAppiusLiveTif: {
    loading: boolean;
    data: T.IGetWatchlistHeliosAppiusLiveTifResponse["data"];
  };
  topLiveSimTakers: {
    loading: boolean;
    data: IGetTopLiveLosersAndTopLiveSimTakersResponse["data"];
  };
  topLiveLosers: {
    loading: boolean;
    data: IGetTopLiveLosersAndTopLiveSimTakersResponse["data"];
  };
  tcpSteps: {
    loading: boolean;
    data: T.ITCPStepsResponse["data"];
  };
  gmCappers: {
    loading: boolean;
    data: T.IGMCappersResponse["data"];
  };
  masterBanList: {
    loading: boolean;
    data: T.IMasterBanListResponse["data"];
  };
}

const initialState: IComplianceSearchAccountsState = {
  accounts_table: {
    data: {
      count: 0,
      rows: [],
    },
    loading: false,
  },
  logins: {
    data: {
      count: 0,
      rows: [],
    },
    loading: false,
  },
  uniqueReqIp: {
    data: [],
    loading: false,
  },
  paymentCards: {
    data: [],
    loading: false,
  },
  plans: {
    loading: false,
    data: {
      accounts: [],
      plans: [],
    },
  },
  plansStatus: {
    loading: false,
    data: [],
  },
  purchasesHistory: {
    data: [],
    loading: false,
  },
  resetsRebillsNewPurchases: {
    data: [],
    loading: false,
  },
  basicAccountDetailsAndSalesValue: {
    data: [],
    loading: false,
  },
  searchOnNameVariations: {
    data: {
      count: 0,
      rows: [],
    },
    loading: false,
  },
  searchOnPhoneVariations: {
    data: {
      count: 0,
      rows: [],
    },
    loading: false,
  },
  rithmicAccounts: {
    data: {
      accounts: [],
      tradedAccounts: [],
      evaluation_completed_history: [],
      trailing_history: [],
    },
    loading: false,
  },
  watchlistSubmissionToProp: {
    data: {
      results: {
        count: 0,
        rows: [],
      },
    },
    loading: false,
  },
  watchlistWithdrawals: {
    data: {
      results: {
        count: 0,
        rows: [],
      },
      updated: {
        initialization_date: "",
      },
    },
    loading: false,
  },
  watchlistFailed: {
    data: {
      results: {
        count: 0,
        rows: [],
      },
      updated: { date: "" },
    },
    loading: false,
  },
  watchlistLiveSimTif: {
    data: {
      results: {
        count: 0,
        rows: [],
      },
      updated: {
        submission_timestamp: "",
      },
    },
    loading: false,
  },
  watchlistNotes: {
    data: {
      results: {
        count: 0,
        rows: [],
      },
      updated: {
        measure_timestamp: "",
      },
    },
    loading: false,
  },
  heliosAccountsInvestigation: {
    loading: false,
    data: {
      passes: {
        count: 0,
        rows: [],
      },
      liveSimTif: {
        count: 0,
        rows: [],
      },
      liveTif: {
        count: 0,
        rows: [],
      },
      withdrawals: {
        count: 0,
        rows: [],
      },
      watchlistFailed: {
        count: 0,
        rows: [],
      },
      gm_cappers: {
        count: 0,
        rows: [],
      },
      tcp_steps: {
        count: 0,
        rows: [],
      },
    },
  },
  pnlCounter: {
    data: {
      updated: "",
      pnlData: [],
    },
    loading: false,
  },
  heliosAndAppiusLiveTif: {
    loading: false,
    data: {
      count: 0,
      rows: [],
      updated: {
        initialization_date: "",
      },
    },
  },
  topLiveSimTakers: {
    loading: false,
    data: {
      results: {
        rows: [],
        count: 0,
      },
    },
  },
  topLiveLosers: {
    loading: false,
    data: {
      results: {
        rows: [],
        count: 0,
      },
    },
  },
  tcpSteps: {
    loading: false,
    data: {
      updated: {
        timestamp: "",
      },
      results: {
        rows: [],
        count: 0,
      },
    },
  },
  gmCappers: {
    loading: false,
    data: {
      updated: {
        timestamp: "",
      },
      results: {
        rows: [],
        count: 0,
      },
    },
  },
  masterBanList: {
    loading: false,
    data: {
      results: {
        rows: [],
        count: 0,
      },
    },
  },
};

const index = createSlice({
  name: "complianceSearch",
  initialState,
  reducers: {
    // SearchAccounts
    fetchAccountsRequest: (state) => {
      state.accounts_table.loading = true;
    },
    fetchAccountsSuccess: (
      state,
      action: PayloadAction<T.IGetAllEntriesResponse["data"]["accounts_table"]>
    ) => {
      state.accounts_table.data = action.payload;
      state.accounts_table.loading = false;
    },
    fetchAccountsFailed: (state) => {
      state.accounts_table.loading = false;
    },

    // Search logins
    fetchLoginsRequest: (state) => {
      state.logins.loading = true;
    },
    fetchLoginsSuccess: (
      state,
      action: PayloadAction<T.IGetCustomersLoginsResponse["data"]["logins"]>
    ) => {
      state.logins.data = action.payload;
      state.logins.loading = false;
    },
    fetchLoginsFailed: (state) => {
      state.logins.loading = false;
    },

    // Search unique req ip
    fetchUniqueReqIpRequest: (state) => {
      state.uniqueReqIp.loading = true;
    },
    fetchUniqueReqIpSuccess: (
      state,
      action: PayloadAction<T.IUniqueReqIpResponse["data"]["usageData"]>
    ) => {
      state.uniqueReqIp.data = action.payload;
      state.uniqueReqIp.loading = false;
    },
    fetchUniqueReqIpFailed: (state) => {
      state.uniqueReqIp.loading = false;
    },

    // SearchPaymentCards
    fetchPaymentCardsRequest: (state) => {
      state.paymentCards.loading = true;
    },
    fetchPaymentCardsSuccess: (
      state,
      action: PayloadAction<T.IGetPaymentCardsResponse["data"]["paymentCards"]>
    ) => {
      state.paymentCards.data = action.payload;
      state.paymentCards.loading = false;
    },
    fetchPaymentCardsFailed: (state) => {
      state.paymentCards.loading = false;
    },

    // SearchPlans
    fetchPlansRequest: (state) => {
      state.plans.loading = true;
    },
    fetchPlansSuccess: (state, action: PayloadAction<T.IGetAccountPlansDataResponse["data"]>) => {
      state.plans.data = action.payload;
      state.plans.loading = false;
    },
    fetchPlansFailed: (state) => {
      state.plans.loading = false;
    },

    // SearchPlansStatus
    fetchPlansStatusRequest: (state) => {
      state.plansStatus.loading = true;
    },
    fetchPlansStatusSuccess: (
      state,
      action: PayloadAction<T.IGetAccountPlansStatusResponse["data"]["accounts"]>
    ) => {
      state.plansStatus.data = action.payload;
      state.plansStatus.loading = false;
    },
    fetchPlansStatusFailed: (state) => {
      state.plansStatus.loading = false;
    },

    // SearchPurchases
    fetchPurchasesRequest: (state) => {
      state.purchasesHistory.loading = true;
    },
    fetchPurchasesSuccess: (
      state,
      action: PayloadAction<T.TGetAccountsPurchasesResponse["data"]["purchasesHistory"]>
    ) => {
      state.purchasesHistory.data = action.payload;
      state.purchasesHistory.loading = false;
    },
    fetchPurchasesFailed: (state) => {
      state.purchasesHistory.loading = false;
    },

    // SearchResetsRebillsNewPurchases
    fetchResetsRebillsNewPurchasesRequest: (state) => {
      state.resetsRebillsNewPurchases.loading = true;
    },
    fetchResetsRebillsNewPurchasesSuccess: (
      state,
      action: PayloadAction<T.IGetAccountsResetsRebillsNewPurchasesResponse["data"]["accounts"]>
    ) => {
      state.resetsRebillsNewPurchases.data = action.payload;
      state.resetsRebillsNewPurchases.loading = false;
    },
    fetchResetsRebillsNewPurchasesFailed: (state) => {
      state.resetsRebillsNewPurchases.loading = false;
    },

    // BasicAccountDetailsAndSalesValue
    fetchBasicAccountDetailsAndSalesValueRequest: (state) => {
      state.basicAccountDetailsAndSalesValue.loading = true;
    },
    fetchBasicAccountDetailsAndSalesValueSuccess: (
      state,
      action: PayloadAction<T.IBasicAccountDetailsAndSalesValueResponse["data"]["accounts"]>
    ) => {
      state.basicAccountDetailsAndSalesValue.data = action.payload;
      state.basicAccountDetailsAndSalesValue.loading = false;
    },
    fetchBasicAccountDetailsAndSalesValueFailed: (state) => {
      state.basicAccountDetailsAndSalesValue.loading = false;
    },

    // AccountSearchOnNameVariations
    fetchByNameRequest: (state) => {
      state.searchOnNameVariations.loading = true;
    },
    fetchByNameSuccess: (
      state,
      action: PayloadAction<T.IGetAllEntriesRequestByValuesResponse["data"]>
    ) => {
      state.searchOnNameVariations.data = action.payload;
      state.searchOnNameVariations.loading = false;
    },
    fetchByNameFailed: (state) => {
      state.searchOnNameVariations.loading = false;
    },

    // AccountSearchByPhone
    fetchByPhoneRequest: (state) => {
      state.searchOnPhoneVariations.loading = true;
    },
    fetchByPhoneSuccess: (
      state,
      action: PayloadAction<T.IGetAllEntriesRequestByValuesResponse["data"]>
    ) => {
      state.searchOnPhoneVariations.data = action.payload;
      state.searchOnPhoneVariations.loading = false;
    },
    fetchByPhoneFailed: (state) => {
      state.searchOnPhoneVariations.loading = false;
    },

    // SearchRithmicAccounts
    fetchRithmicAccountsRequest: (state) => {
      state.rithmicAccounts.loading = true;
    },
    fetchRithmicAccountsSuccess: (
      state,
      action: PayloadAction<T.IGetEvaluationsRithmicAccountsResponse["data"]>
    ) => {
      state.rithmicAccounts.data = action.payload;
      state.rithmicAccounts.loading = false;
    },
    fetchRithmicAccountsFailed: (state) => {
      state.rithmicAccounts.loading = false;
    },

    // WatchlistPasses
    fetchWatchlistPassesRequest: (state) => {
      state.watchlistSubmissionToProp.loading = true;
    },
    fetchWatchlistPassesSuccess: (state, action: PayloadAction<T.IGetPassesResponse["data"]>) => {
      state.watchlistSubmissionToProp.data = action.payload;
      state.watchlistSubmissionToProp.loading = false;
    },
    fetchWatchlistPassesFailed: (state) => {
      state.watchlistSubmissionToProp.loading = false;
    },

    // WatchlistWithdrawals
    fetchWatchlistWithdrawalsRequest: (state) => {
      state.watchlistWithdrawals.loading = true;
    },
    fetchWatchlistWithdrawalsSuccess: (
      state,
      action: PayloadAction<T.IWithdrawalsResponse["data"]>
    ) => {
      state.watchlistWithdrawals.data = action.payload;
      state.watchlistWithdrawals.loading = false;
    },
    fetchWatchlistWithdrawalsFailed: (state) => {
      state.watchlistWithdrawals.loading = false;
    },

    // WatchlistFailed
    fetchWatchlistFailedRequest: (state) => {
      state.watchlistFailed.loading = true;
    },
    fetchWatchlistFailedSuccess: (state, action: PayloadAction<T.IFailedResponse["data"]>) => {
      state.watchlistFailed.data = action.payload;
      state.watchlistFailed.loading = false;
    },
    fetchWatchlistFailedFailed: (state) => {
      state.watchlistFailed.loading = false;
    },

    // WatchlistLiveSimTif
    fetchWatchlistLiveSimTifRequest: (state) => {
      state.watchlistLiveSimTif.loading = true;
    },
    fetchWatchlistLiveSimTifSuccess: (
      state,
      action: PayloadAction<T.IGetWatchlistLiveSimTifResponse["data"]>
    ) => {
      state.watchlistLiveSimTif.data = action.payload;
      state.watchlistLiveSimTif.loading = false;
    },
    fetchWatchlistLiveSimTifFailed: (state) => {
      state.watchlistLiveSimTif.loading = false;
    },

    // WatchlistNotes
    fetchWatchlistNotesRequest: (state) => {
      state.watchlistNotes.loading = true;
    },
    fetchWatchlistNotesSuccess: (
      state,
      action: PayloadAction<T.IGetSearchNotesResponse["data"]>
    ) => {
      state.watchlistNotes.data = action.payload;
      state.watchlistNotes.loading = false;
    },
    fetchWatchlistNotesFailed: (state) => {
      state.watchlistNotes.loading = false;
    },

    // HeliosAccountsInvestigation
    fetchHeliosAccountsInvestigationRequest: (state) => {
      state.heliosAccountsInvestigation.loading = true;
    },
    fetchHeliosAccountsInvestigationSuccess: (
      state,
      action: PayloadAction<T.IGetHeliosRelatedSearchResponse["data"]>
    ) => {
      state.heliosAccountsInvestigation.data = action.payload;
      state.heliosAccountsInvestigation.loading = false;
    },
    fetchHeliosAccountsInvestigationFailed: (state) => {
      state.heliosAccountsInvestigation.loading = false;
    },

    // PnLCounter
    fetchPnLCounterRequest: (state) => {
      state.pnlCounter.loading = true;
    },
    fetchPnLCounterSuccess: (state, action: PayloadAction<T.IGetPnlDataResponse["data"]>) => {
      state.pnlCounter.data = action.payload;
      state.pnlCounter.loading = false;
    },
    fetchPnLCounterFailed: (state) => {
      state.pnlCounter.loading = false;
    },

    // HeliosAndAppiusLiveTif
    fetchWatchlistHeliosAndAppiusLiveTifRequest: (state) => {
      state.heliosAndAppiusLiveTif.loading = true;
    },
    fetchWatchlistHeliosAndAppiusLiveTifSuccess: (
      state,
      action: PayloadAction<T.IGetWatchlistHeliosAppiusLiveTifResponse["data"]>
    ) => {
      state.heliosAndAppiusLiveTif.data = action.payload;
      state.heliosAndAppiusLiveTif.loading = false;
    },
    fetchWatchlistHeliosAndAppiusLiveTifFailed: (state) => {
      state.heliosAndAppiusLiveTif.loading = false;
    },

    // TopLiveSimTakers
    fetchTopLiveSimTakersRequest: (state) => {
      state.topLiveSimTakers.loading = true;
    },
    fetchTopLiveSimTakersSuccess: (
      state,
      action: PayloadAction<IGetTopLiveLosersAndTopLiveSimTakersResponse["data"]>
    ) => {
      state.topLiveSimTakers.data = action.payload;
      state.topLiveSimTakers.loading = false;
    },
    fetchTopLiveSimTakersFailed: (state) => {
      state.topLiveSimTakers.loading = false;
    },

    // TopLiveLosers
    fetchTopLiveLosersRequest: (state) => {
      state.topLiveLosers.loading = true;
    },
    fetchTopLiveLosersSuccess: (
      state,
      action: PayloadAction<IGetTopLiveLosersAndTopLiveSimTakersResponse["data"]>
    ) => {
      state.topLiveLosers.data = action.payload;
      state.topLiveLosers.loading = false;
    },
    fetchTopLiveLosersFailed: (state) => {
      state.topLiveLosers.loading = false;
    },

    // TCPSteps
    fetchTCPStepsRequest: (state) => {
      state.tcpSteps.loading = true;
    },
    fetchTCPStepsSuccess: (state, action: PayloadAction<T.ITCPStepsResponse["data"]>) => {
      state.tcpSteps.data = action.payload;
      state.tcpSteps.loading = false;
    },
    fetchTCPStepsFailed: (state) => {
      state.tcpSteps.loading = false;
    },

    // GMCappers
    fetchGMCappersRequest: (state) => {
      state.gmCappers.loading = true;
    },
    fetchGMCappersSuccess: (state, action: PayloadAction<T.IGMCappersResponse["data"]>) => {
      state.gmCappers.data = action.payload;
      state.gmCappers.loading = false;
    },
    fetchGMCappersFailed: (state) => {
      state.gmCappers.loading = false;
    },

    // MasterBanList
    fetchMasterBanListRequest: (state) => {
      state.masterBanList.loading = true;
    },
    fetchMasterBanListSuccess: (state, action: PayloadAction<T.IMasterBanListResponse["data"]>) => {
      state.masterBanList.data = action.payload;
      state.masterBanList.loading = false;
    },
    fetchMasterBanListFailed: (state) => {
      state.masterBanList.loading = false;
    },
  },
});

export const complianceSearchActions = index.actions;
export default index.reducer;
